import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { useCallback, useState } from 'react';

/**
 * Acquire the device's current GPS position.
 *
 * Uses `@capacitor/geolocation` on native iOS/Android (handles the
 * platform permission prompt + uses the OS location services
 * directly) and falls back to `navigator.geolocation` in the
 * browser.  Both paths normalize to a common shape:
 *
 *   `{ latitude, longitude }` rounded to the requested precision
 *   (default 6 decimals — ~10 cm of resolution, matching how the
 *   rest of the portal stores site coordinates).
 *
 * Returns a stable `request()` plus reactive `status` / `coords` /
 * `error` state.  Status transitions: `idle` → `requesting` →
 * `success` | `error` | `unsupported`.  Re-`request()` resets to
 * `requesting` and resolves to a fresh fix.
 */
export default function useDeviceGeolocation({ precision = 6, enableHighAccuracy = true, timeout = 5000 } = {}) {
  const [status, setStatus] = useState('idle');
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);

  const round = useCallback(
    position => ({
      latitude: parseFloat(position.coords.latitude.toFixed(precision)),
      longitude: parseFloat(position.coords.longitude.toFixed(precision)),
    }),
    [precision],
  );

  const request = useCallback(async () => {
    setStatus('requesting');
    setError(null);

    // Native: prefer Capacitor — it talks to the OS location service
    // directly and handles iOS/Android permission prompts for us.
    if (Capacitor.isNativePlatform()) {
      try {
        const position = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout });
        const next = round(position);
        setCoords(next);
        setStatus('success');
        return next;
      } catch (err) {
        setError(err);
        setStatus('error');
        return null;
      }
    }

    // Web fallback.
    if (!navigator?.geolocation) {
      setStatus('unsupported');
      setError(new Error('Geolocation not supported by this browser'));
      return null;
    }

    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          const next = round(position);
          setCoords(next);
          setStatus('success');
          resolve(next);
        },
        err => {
          setError(err);
          setStatus('error');
          resolve(null);
        },
        { enableHighAccuracy, timeout, maximumAge: 0 },
      );
    });
  }, [round, enableHighAccuracy, timeout]);

  return { coords, status, error, request };
}
